import React from "react";
import { Box, Card, Grid, Skeleton, Stack, Typography } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BookmarkAddedOutlinedIcon from "@mui/icons-material/BookmarkAddedOutlined";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";

type Summary = {
  total_students?: number;
  students_with_subjects?: number;
  students_without_subjects?: number;
  total_active_enrolments?: number;
};

// Four coloured summary cards. Responsive: 4 across desktop, 2 tablet, 1 mobile.
// Compact height (fixed padding so all four match) with an icon block, big
// number and a short supporting caption. Skeletons while the summary loads.
const CARDS = [
  { key: "total_students",           label: "Total Students",     sub: "Students matching the current filters",
    color: "#1b3a6b", bg: "#eff6ff", Icon: PeopleAltOutlinedIcon },
  { key: "students_with_subjects",   label: "With Subjects",      sub: "Have at least one enrolled subject",
    color: "#166534", bg: "#dcfce7", Icon: BookmarkAddedOutlinedIcon },
  { key: "students_without_subjects",label: "Without Subjects",   sub: "No subjects assigned yet",
    color: "#92400e", bg: "#fef3c7", Icon: PriorityHighOutlinedIcon },
  { key: "total_active_enrolments",  label: "Total Enrolments",   sub: "Sum of active grade-subject rows",
    color: "#6b21a8", bg: "#f3e8ff", Icon: LibraryBooksOutlinedIcon },
] as const;

export default function SummaryCards({ summary, loading }: { summary: Summary; loading: boolean }) {
  return (
    <Grid container spacing={1.5} mb={2}>
      {CARDS.map(({ key, label, sub, color, bg, Icon }) => (
        <Grid key={key} item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                width: 44, height: 44, borderRadius: "12px",
                background: bg, color, display: "flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <Icon />
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                {loading ? <Skeleton width="60%" height={28} /> : (
                  <Typography variant="h5" fontWeight={700} color={color} lineHeight={1.15}>
                    {(summary[key as keyof Summary] ?? 0).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {sub}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
